import {
    CancelledError,
    queryOptions,
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";

import { isAxiosNotFoundError } from "@/api/errors";
import { getAuthSessionEpoch } from "@/contexts/auth/AuthContext";
import {
    getConversationReports,
    retryReport,
    type ReportRetryError,
} from "@/api/reports/reports";
import type {
    IReportVersionSummary,
    ReportVersionStatus,
} from "@/api/reports/reports.types";

export const REPORT_PREPARING_POLL_MS = 5_000;
export const REPORT_WAITING_POLL_MS = 30_000;
export const REPORT_MAX_ERROR_POLL_MS = 5 * 60_000;

export const conversationReportsQueryKey = (conversationId: number) =>
    ["conversation-reports", conversationId] as const;

export const retryReportMutationKey = (
    conversationId: number,
    versionId: number
) => ["retry-report", conversationId, versionId] as const;

type ReportPollState = {
    status: "pending" | "error" | "success";
    error: unknown;
    data: IReportVersionSummary[] | undefined;
    errorUpdateCount: number;
};

/**
 * `errorUpdateCount` at each cached list's last successful read, whoever made
 * it. Kept with the cached query, so it outlives the view and is dropped when
 * the query is collected, which also restarts its count.
 */
const errorCountAtLastSuccess = new WeakMap<object, number>();

/** Per client and conversation, the start order of the latest read to answer with rows. */
const latestAnsweredRead = new WeakMap<QueryClient, Map<number, number>>();
let reportReadsStarted = 0;

function laterReadAnswered(
    client: QueryClient,
    conversationId: number,
    readOrder: number
): boolean {
    return (
        (latestAnsweredRead.get(client)?.get(conversationId) ?? 0) > readOrder
    );
}

/** Record an answered read; false when a read that started later has answered. */
function answeredLatest(
    client: QueryClient,
    conversationId: number,
    readOrder: number
): boolean {
    if (laterReadAnswered(client, conversationId, readOrder)) return false;
    const answered = latestAnsweredRead.get(client) ?? new Map();
    latestAnsweredRead.set(client, answered);
    answered.set(conversationId, readOrder);
    return true;
}

function recordReportReadSuccess(
    client: QueryClient,
    conversationId: number
): IReportVersionSummary[] | undefined {
    const query = client.getQueryCache().find<IReportVersionSummary[]>({
        queryKey: conversationReportsQueryKey(conversationId),
        exact: true,
    });
    if (!query) return undefined;
    errorCountAtLastSuccess.set(query, query.state.errorUpdateCount);
    return query.state.data;
}

/**
 * A read that answers, with rows or with a failure, after a read that started
 * later has answered gives the newer cached rows, so it changes neither the
 * list nor its error state.
 */
export const conversationReportsQueryOptions = (conversationId: number) =>
    queryOptions({
        queryKey: conversationReportsQueryKey(conversationId),
        queryFn: async ({ client, signal }) => {
            const readOrder = (reportReadsStarted += 1);
            let rows: IReportVersionSummary[];
            try {
                rows = await getConversationReports(conversationId, signal);
            } catch (error) {
                if (!laterReadAnswered(client, conversationId, readOrder)) {
                    throw error;
                }
                const newer = recordReportReadSuccess(client, conversationId);
                if (newer === undefined) throw error;
                return newer;
            }
            const cached = recordReportReadSuccess(client, conversationId);
            return answeredLatest(client, conversationId, readOrder)
                ? rows
                : (cached ?? rows);
        },
    });

const PREPARING_STATUSES = new Set<ReportVersionStatus>([
    "pending",
    "processing",
]);

export function reportPollInterval(
    state: ReportPollState,
    consecutiveErrors: number
): number | false {
    if (state.status === "error") {
        if (isAxiosNotFoundError(state.error)) return false;
        const backoff =
            REPORT_PREPARING_POLL_MS * 2 ** Math.max(0, consecutiveErrors - 1);
        return Math.min(backoff, REPORT_MAX_ERROR_POLL_MS);
    }
    const rows = state.data ?? [];
    if (rows.some((row) => PREPARING_STATUSES.has(row.status))) {
        return REPORT_PREPARING_POLL_MS;
    }
    if (rows.some((row) => row.status === "waiting")) {
        return REPORT_WAITING_POLL_MS;
    }
    return false;
}

/**
 * Count the cached list's consecutive failed reads: `errorUpdateCount` grows
 * once per failed fetch and is measured from its value at the last success.
 * The failure count React Query keeps restarts at every fetch, and a list that
 * never loaded is `pending` again while each retry runs, so neither marks a
 * recovery.
 */
export function consecutiveReportReadFailures(query: {
    state: Pick<ReportPollState, "errorUpdateCount">;
}): number {
    return Math.max(
        0,
        query.state.errorUpdateCount - (errorCountAtLastSuccess.get(query) ?? 0)
    );
}

export function useConversationReports(conversationId: number) {
    return useQuery({
        ...conversationReportsQueryOptions(conversationId),
        refetchInterval: (query) =>
            reportPollInterval(
                query.state,
                consecutiveReportReadFailures(query)
            ),
    });
}

/** Fetch the list into the shared cache, joining a read already in flight. */
export function readConversationReports(
    queryClient: QueryClient,
    conversationId: number
): Promise<IReportVersionSummary[]> {
    return queryClient.fetchQuery({
        ...conversationReportsQueryOptions(conversationId),
        staleTime: 0,
    });
}

/** A fresh read's own query, under a root of its own so no list invalidation refetches it. */
const freshReportReadQueryKey = (conversationId: number, readOrder: number) =>
    ["conversation-reports-fresh-read", conversationId, readOrder] as const;

/**
 * Read the list with a request of the caller's own that starts now, so no read
 * that began before a write can answer for it. Reads already in flight run on
 * and keep their own callers; the cache takes these rows unless a read that
 * started later has answered. A failure leaves the cache and, unless a later
 * read has answered, has mounted lists read again. The request runs as a query
 * of the client, so clearing the client cancels it, and an answer that arrives
 * after the auth session changed is dropped as cancelled.
 */
export async function readFreshConversationReports(
    queryClient: QueryClient,
    conversationId: number
): Promise<IReportVersionSummary[]> {
    const readOrder = (reportReadsStarted += 1);
    const sessionEpoch = getAuthSessionEpoch();
    const queryKey = conversationReportsQueryKey(conversationId);
    let rows: IReportVersionSummary[];
    try {
        rows = await queryClient.fetchQuery({
            queryKey: freshReportReadQueryKey(conversationId, readOrder),
            queryFn: ({ signal }) =>
                getConversationReports(conversationId, signal),
            gcTime: 0,
        });
    } catch (error) {
        if (!laterReadAnswered(queryClient, conversationId, readOrder)) {
            void queryClient.invalidateQueries(
                { queryKey, exact: true },
                { cancelRefetch: false }
            );
        }
        throw error;
    }
    if (getAuthSessionEpoch() !== sessionEpoch) throw new CancelledError();
    if (answeredLatest(queryClient, conversationId, readOrder)) {
        queryClient.setQueryData(queryKey, rows);
        recordReportReadSuccess(queryClient, conversationId);
    }
    return rows;
}

export type ReportRetryOutcome =
    | { kind: "confirmed"; version: IReportVersionSummary }
    | { kind: "divergent"; version: IReportVersionSummary | null }
    | { kind: "unconfirmed"; cause: unknown };

const RETRY_CONFIRMING_STATUSES = new Set<ReportVersionStatus>([
    "waiting",
    "pending",
    "processing",
    "completed",
]);

/** Decide a committed Retry from a list read that starts after the write. */
export async function confirmReportRetry(
    queryClient: QueryClient,
    conversationId: number,
    versionId: number
): Promise<ReportRetryOutcome> {
    let rows: IReportVersionSummary[];
    try {
        rows = await readFreshConversationReports(queryClient, conversationId);
    } catch (cause) {
        return { kind: "unconfirmed", cause };
    }
    const version = rows.find((row) => row.id === versionId) ?? null;
    if (version && RETRY_CONFIRMING_STATUSES.has(version.status)) {
        return { kind: "confirmed", version };
    }
    return { kind: "divergent", version };
}

/** Each client's Retry in flight per conversation and version, joined by every consumer. */
const retriesInFlight = new WeakMap<
    QueryClient,
    Map<string, Promise<ReportRetryOutcome>>
>();

export function useRetryReport(conversationId: number, versionId: number) {
    const queryClient = useQueryClient();
    return useMutation<ReportRetryOutcome, ReportRetryError | Error>({
        mutationKey: retryReportMutationKey(conversationId, versionId),
        mutationFn: () => {
            const inFlight =
                retriesInFlight.get(queryClient) ??
                new Map<string, Promise<ReportRetryOutcome>>();
            retriesInFlight.set(queryClient, inFlight);
            const key = `${conversationId}:${versionId}`;
            const running = inFlight.get(key);
            if (running) return running;
            const retry = (async () => {
                await retryReport(conversationId, versionId);
                return confirmReportRetry(
                    queryClient,
                    conversationId,
                    versionId
                );
            })().finally(() => inFlight.delete(key));
            inFlight.set(key, retry);
            return retry;
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: conversationReportsQueryKey(conversationId),
                exact: true,
            });
        },
    });
}

export function useIsReportRetrying(
    conversationId: number,
    versionId: number
): boolean {
    return (
        useIsMutating({
            mutationKey: retryReportMutationKey(conversationId, versionId),
            exact: true,
        }) > 0
    );
}
